<template>
    <select-label
        v-model="mode"
        :items="passwordMode"
        :disabled="isDisabled"
        :append-icon="'$vuetify.icons.dropdown'"
        item-text="text"
        item-value="value"
    />
</template>

<script setup lang="ts">
import { toRefs, ref, readonly, computed, watch } from 'vue';
import SelectLabel from './SelectLabel.vue';
import { useTranslation } from '../../composables/translation';
import { AppContext } from '../../store/context';

/*
 * properties
 */
interface Props {
    value?: string;
    isSshKeySelectable: boolean;
    isUsedSshKey: boolean;
    disabled: boolean;
}
const props = withDefaults(defineProps<Props>(), {
    value: '',
    isSshKeySelectable: false,
    isUsedSshKey: false,
    disabled: false,
});
const { value, isSshKeySelectable, isUsedSshKey, disabled } = toRefs(props);

/**
 * composables
 */
const { t } = useTranslation();
const mode = ref('');

/*
 * emits
 */
interface Emits {
    (e: 'input', param: string): void;
}
const emit = defineEmits<Emits>();

/*
 * getter, setter
 */
const isDisabled = readonly(disabled);
const connectionTypePassword = computed(() => {
    return [
        {
            text: t(AppContext.ConnectionType.PASSWORD.text_key),
            value: AppContext.ConnectionType.PASSWORD.value,
        },
    ];
});
const connectionTypeSshKey = computed(() => {
    return [
        {
            text: t(AppContext.ConnectionType.SSH_KEY.text_key),
            value: AppContext.ConnectionType.SSH_KEY.value,
        },
    ];
});
const connectionTypeList = computed(() => {
    return AppContext.ConnectionTypeList.map(kv => {
        return {
            text: t(kv.text_key),
            value: kv.value,
        };
    });
});

const passwordMode = computed(() => {
    return isSshKeySelectable.value
        ? isUsedSshKey.value
            ? connectionTypeSshKey.value
            : connectionTypeList.value
        : connectionTypePassword.value;
});

/**
 * watchers
 */
watch(
    value,
    (newVal: string) => {
        mode.value = newVal;
    },
    { immediate: true }
);
watch(mode, (newVal: string) => {
    emit('input', newVal);
});
</script>
